// AI Copilot panel — conversational world building. Executes the agent's
// actions through the same store/broadcast/persistence pipeline as human
// builders, so copilot edits are live-synced and saved for everyone.

import React, { useState, useRef, useEffect } from 'react';
import { X, Sparkles, Send, Loader } from 'lucide-react';
import { putObject } from '../utils/uploadToStorage';
import { useWorldStore } from '../stores/worldStore';
import { broadcastObjectAdd, broadcastObjectUpdate, broadcastObjectDelete } from '../services/worldBroadcast';
import { searchModels, fetchModelBlob, PolyModel } from '../services/polyPizzaApi';
import { generateMuseSky } from '../services/museSky';
import { runCopilot, CopilotAction } from '../services/copilotAgent';
import { generateObjectImage } from '../services/openRouterApi';
import { generateObjectFromImage } from '../services/meshyApi';

interface CopilotPanelProps {
  worldId: string;
  onClose: () => void;
}

interface ChatMsg {
  who: 'user' | 'ai' | 'log';
  text: string;
}

const CopilotPanel: React.FC<CopilotPanelProps> = ({ worldId, onClose }) => {
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [meshyOnly, setMeshyOnly] = useState(false);
  const [chat, setChat] = useState<ChatMsg[]>([
    { who: 'ai', text: 'Tell me what to build — "a cozy campfire circle with logs to sit on", "a small stage with warm lighting", "add six trees around the edge"...' },
  ]);
  const historyRef = useRef<{ role: string; content: string }[]>([]);
  const searchCache = useRef<Map<string, PolyModel>>(new Map());
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat]);

  const log = (who: ChatMsg['who'], text: string) => setChat((c) => [...c, { who, text }]);

  const placeModel = async (model: PolyModel, position: [number, number, number], scale: number) => {
    const blob = await fetchModelBlob(model.downloadUrl);
    let url = model.downloadUrl;
    try {
      const key = `objects/copilot-${model.id}-${Date.now()}.glb`;
      url = await putObject(key, blob, 'model/gltf-binary');
    } catch { /* fall back to source url */ }
    const store = useWorldStore.getState();
    const id = `content-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const item = { url, type: 'model' as const, position, scale, rotation: [0, 0, 0] as [number, number, number], visible: true, isLocked: false, collidable: true, filename: model.title, uploadedAt: Date.now() };
    store.addContentItem(id, item);
    broadcastObjectAdd(worldId, 'content', id, item);
    return id;
  };

  const execute = async (a: CopilotAction): Promise<string> => {
    const store = useWorldStore.getState();
    switch (a.action) {
      case 'add_primitive': {
        const id = `prim-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const item = {
          shape: a.shape || 'box', name: a.name || a.shape || 'Object', color: a.color || '#888888',
          position: (a.position || [0, 0.5, 0]) as [number, number, number],
          rotation: (a.rotation || [0, 0, 0]) as [number, number, number],
          scale: (a.scale || [1, 1, 1]) as [number, number, number],
          texturePreset: null, textureUrl: null, pbrMaps: null, textureScale: 2,
          visible: true, isLocked: false, collidable: true, colliderOnly: false,
          opacity: 1, metalness: 0, roughness: 0.6,
        };
        store.addPrimitive(id, item as any);
        broadcastObjectAdd(worldId, 'primitive', id, item);
        log('log', `▸ added ${item.shape} "${item.name}"`);
        return `added ${id}`;
      }
      case 'search_models': {
        const res = await searchModels(a.query || '', 0, 6);
        res.models.forEach((m) => searchCache.current.set(m.id, m));
        log('log', `▸ searched "${a.query}" — ${res.models.length} results`);
        return `search "${a.query}": ${res.models.map((m) => `${m.id}="${m.title}"`).join(', ') || 'no results'}`;
      }
      case 'place_model': {
        const model = searchCache.current.get(String(a.result_id));
        if (!model) return `ERROR: unknown result_id ${a.result_id}`;
        const id = await placeModel(model, (a.position || [0, 0, 0]) as [number, number, number], a.scale ?? 1);
        log('log', `▸ placed "${model.title}"`);
        return `placed ${id} ("${model.title}")`;
      }
      case 'move_object': {
        const id = String(a.id);
        const pos = a.position as [number, number, number];
        if (store.primitives[id]) { store.updatePrimitive(id, { position: pos }); broadcastObjectUpdate(worldId, 'primitive', id, { position: pos }); }
        else if (store.contentItems[id]) { store.updateContentItem(id, { position: pos }); broadcastObjectUpdate(worldId, 'content', id, { position: pos }); }
        else if (store.lights[id]) { store.updateLight(id, { position: pos }); broadcastObjectUpdate(worldId, 'light', id, { position: pos }); }
        else return `ERROR: no object ${id}`;
        log('log', `▸ moved ${id}`);
        return `moved ${id}`;
      }
      case 'delete_object': {
        const id = String(a.id);
        if (store.primitives[id]) { store.deletePrimitive(id); broadcastObjectDelete(worldId, 'primitive', id); }
        else if (store.contentItems[id]) { store.deleteContentItem(id); broadcastObjectDelete(worldId, 'content', id); }
        else if (store.lights[id]) { store.deleteLight(id); broadcastObjectDelete(worldId, 'light', id); }
        else return `ERROR: no object ${id}`;
        log('log', `▸ deleted ${id}`);
        return `deleted ${id}`;
      }
      case 'set_sky': {
        log('log', `▸ generating sky: "${a.prompt}" (~30s)`);
        // Muse (free on the beta allowance). Skybox AI costs Studio credits, so
        // it's only ever started by the user from the Sky panel.
        const sky = await generateMuseSky(String(a.prompt || 'clear blue sky'));
        const id = `skybox-copilot-${Date.now()}`;
        store.addSkybox(id, { url: sky.url, filename: sky.filename, uploadedAt: Date.now() });
        store.setCurrentSkyboxId(id);
        log('log', '▸ sky applied');
        return 'sky set';
      }
      case 'generate_model': {
        const desc = String(a.description || 'an object');
        log('log', `▸ generating "${desc.slice(0, 60)}" with Meshy (~2 min)`);
        const img = await generateObjectImage(desc);
        // Meshy needs a fetchable URL — upload the reference image to R2
        // (raw multi-MB data URIs get rejected)
        const imgBlob = await (await fetch(img.dataUrl)).blob();
        const imgKey = `objects/ref-${Date.now()}.png`;
        const imgUrl = await putObject(imgKey, imgBlob, 'image/png');
        const gen = await generateObjectFromImage(imgUrl, {
          onStage: (st) => { if (st === 'generating') log('log', '  … converting to 3D'); },
        });
        // Meshy URLs expire — copy the GLB into our storage (proxy: no CORS on Meshy)
        let blob: Blob;
        try {
          const r = await fetch('/api/proxy-fetch', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: gen.glbUrl }),
          });
          if (!r.ok) throw new Error(String(r.status));
          blob = await r.blob();
        } catch {
          blob = await (await fetch(gen.glbUrl)).blob();
        }
        let url = gen.glbUrl;
        try {
          const key = `objects/meshy-${Date.now()}.glb`;
          url = await putObject(key, blob, 'model/gltf-binary');
        } catch { /* fall back to Meshy URL */ }
        const genStore = useWorldStore.getState();
        const gid = `content-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const gitem = { url, type: 'model' as const, position: (a.position || [0, 0, 0]) as [number, number, number], scale: a.scale ?? 1, rotation: [0, 0, 0] as [number, number, number], visible: true, isLocked: false, collidable: true, filename: desc.slice(0, 40), uploadedAt: Date.now() };
        genStore.addContentItem(gid, gitem);
        broadcastObjectAdd(worldId, 'content', gid, gitem);
        log('log', `▸ generated + placed "${desc.slice(0, 40)}"`);
        return `generated ${gid} ("${desc.slice(0, 40)}")`;
      }
      case 'say':
        return '';
      default:
        return `ERROR: unknown action ${(a as any).action}`;
    }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput('');
    log('user', msg);
    setBusy(true);
    try {
      historyRef.current = await runCopilot(msg, historyRef.current, execute, (say) => log('ai', say), { meshyOnly });
    } catch (err: any) {
      log('ai', `Something went wrong: ${err?.message || 'unknown error'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'absolute', top: 70, right: 16, width: 360, maxHeight: 'calc(100vh - 160px)',
      display: 'flex', flexDirection: 'column', zIndex: 300,
      background: 'rgba(10, 22, 38, 0.92)', backdropFilter: 'blur(16px)',
      border: '1px solid rgba(0,194,179,0.3)', borderRadius: 16,
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)', fontFamily: 'Outfit, sans-serif',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#eaf6f8', fontWeight: 700, fontSize: 15 }}>
          <Sparkles size={16} color="#00c2b3" /> Build Copilot
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}><X size={16} /></button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 180 }}>
        {chat.map((m, i) => (
          <div key={i} style={{
            alignSelf: m.who === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '92%',
            padding: m.who === 'log' ? '2px 4px' : '8px 12px',
            borderRadius: 12,
            fontSize: m.who === 'log' ? 11.5 : 13.5,
            lineHeight: 1.45,
            background: m.who === 'user' ? 'rgba(0,194,179,0.18)' : m.who === 'ai' ? 'rgba(255,255,255,0.06)' : 'transparent',
            color: m.who === 'log' ? 'rgba(0,194,179,0.75)' : '#eaf6f8',
          }}>
            {m.text}
          </div>
        ))}
        {busy && <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}><Loader size={12} className="pbr-spin" /> building...</div>}
        <div ref={endRef} />
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px 0', fontSize: 12, color: 'rgba(255,255,255,0.6)', cursor: 'pointer', userSelect: 'none' }}>
        <input type="checkbox" checked={meshyOnly} onChange={(e) => setMeshyOnly(e.target.checked)} style={{ accentColor: '#00c2b3' }} />
        Meshy only — generate every model with AI (slower, uses credits)
      </label>
      <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder={busy ? 'Building...' : 'Describe what to build...'}
          disabled={busy}
          style={{
            flex: 1, padding: '10px 12px', borderRadius: 10, fontSize: 13.5,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#eaf6f8',
          }}
        />
        <button
          onClick={send}
          disabled={busy || !input.trim()}
          style={{
            padding: '0 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg, #00c2b3, #0077b6)', color: '#fff',
            opacity: busy || !input.trim() ? 0.5 : 1,
          }}
        >
          <Send size={15} />
        </button>
      </div>
    </div>
  );
};

export default CopilotPanel;
